/**
 * StaffAlertsWidget — compact admin-only banner for the Dashboard.
 *
 * Shows pending staff decisions (offboarding_pending, needs_onboarding,
 * needs_review) when there is at least one. Links directly to the filtered
 * Staff Members page so the admin can act immediately.
 *
 * Rendered only for admins (caller gates via useIsAdmin). Fetches people
 * from the same query key as AdminPeoplePage so the two share the cache —
 * navigating to Staff Members after seeing this banner shows the same data
 * without a second round-trip.
 */
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { fetchPeople } from "../operations/admin/api";
import type { PersonSummary } from "../operations/admin/types";

function countAlerts(people: PersonSummary[]): {
  offboarding_pending: number;
  needs_onboarding: number;
  needs_review: number;
  total: number;
} {
  const counts = { offboarding_pending: 0, needs_onboarding: 0, needs_review: 0, total: 0 };
  for (const p of people) {
    if (p.status === "offboarding_pending") { counts.offboarding_pending++; counts.total++; }
    else if (p.status === "needs_onboarding") { counts.needs_onboarding++; counts.total++; }
    else if (p.status === "alias_mismatch") { counts.needs_review++; counts.total++; }
  }
  return counts;
}

export function StaffAlertsWidget() {
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ["admin-people"],
    queryFn: fetchPeople,
    staleTime: 60_000,
    retry: false,
  });

  if (isLoading || !data) return null;

  const counts = countAlerts(data);
  if (counts.total === 0) return null;

  return (
    <div className="flex items-start gap-3 rounded-md border border-orange-300 bg-orange-50 px-4 py-3 text-orange-900 dark:border-orange-700 dark:bg-orange-950 dark:text-orange-200">
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-orange-500" aria-hidden="true" />
      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-medium">
          {counts.total} {counts.total === 1 ? "person needs" : "people need"} attention
        </span>
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm">
          {counts.needs_onboarding > 0 && (
            <button
              type="button"
              className="underline underline-offset-2 hover:no-underline"
              onClick={() => navigate("/operations/administration/staff-members")}
            >
              {counts.needs_onboarding} need onboarding
            </button>
          )}
          {counts.offboarding_pending > 0 && (
            <button
              type="button"
              className="underline underline-offset-2 hover:no-underline"
              onClick={() => navigate("/operations/administration/staff-members")}
            >
              {counts.offboarding_pending} offboarding pending
            </button>
          )}
          {counts.needs_review > 0 && (
            <button
              type="button"
              className="underline underline-offset-2 hover:no-underline"
              onClick={() => navigate("/operations/administration/staff-members")}
            >
              {counts.needs_review} need review
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
